import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import SideMenu from '../components/layout/SideMenu';
import Alert from '../components/common/Alert';
import Pagination from '../components/common/Pagination';
import SectorFormModal from '../components/products/SectorFormModal';
import SectorCard from '../components/products/SectorCard';
import ViewToggle from '../components/products/ViewToggle';
import { useAuth } from '../contexts/AuthContext';
import { getSectorsWithStores, updateSector, deleteSector, type SectorWithStores } from '../services/sectorService';
import { extractApiError } from '../utils/errorUtils';
import { ViewMode, UpdateSectorPayload, CreateSectorPayload } from '../types/sector';
import styles from './AllSectorsPage.module.css';
import tableStyles from '../styles/shared/DataTable.module.css';
import cardGridStyles from '../styles/shared/CardGrid.module.css';
import layoutStyles from '../styles/layout.module.css';
import placeholderImage from '../assets/placeholder.png';

const DEFAULT_LIMIT = 20;

const AllSectorsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role?.role_name?.toLowerCase() === 'admin';

  const [sectors, setSectors] = useState<SectorWithStores[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'error'; isVisible: boolean }>({
    message: '',
    type: 'success',
    isVisible: false,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSector, setEditingSector] = useState<SectorWithStores | null>(null);

  const fetchSectors = useCallback(async () => {
    if (!isAdmin) return;
    setIsLoading(true);
    try {
      const data = await getSectorsWithStores({
        page,
        limit,
        search: search || undefined,
        sortBy,
        sortOrder,
      });
      setSectors(data.items);
      setTotal(data.total);
    } catch (err) {
      setSectors([]);
      setTotal(0);
      setAlert({ message: extractApiError(err, 'Failed to load sectors.'), type: 'error', isVisible: true });
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, page, limit, search, sortBy, sortOrder]);

  useEffect(() => {
    fetchSectors();
  }, [fetchSectors]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const totalPages = Math.ceil(total / limit) || 1;
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };
  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  useEffect(() => {
    if (alert.isVisible) {
      const t = setTimeout(() => setAlert((a) => ({ ...a, isVisible: false })), 3000);
      return () => clearTimeout(t);
    }
  }, [alert.isVisible]);

  const hideAlert = () => setAlert((a) => ({ ...a, isVisible: false }));
  const showAlert = (message: string, type: 'success' | 'error') =>
    setAlert({ message, type, isVisible: true });

  const handleEdit = (sector: SectorWithStores) => {
    setEditingSector(sector);
    setIsModalOpen(true);
    hideAlert();
  };

  const handleDelete = async (sectorId: number) => {
    if (!window.confirm('Are you sure you want to delete this sector? This may affect products.')) return;
    hideAlert();
    try {
      await deleteSector(sectorId);
      setSectors((prev) => prev.filter((s) => s.id !== sectorId));
      setTotal((t) => Math.max(0, t - 1));
      showAlert('Sector deleted successfully.', 'success');
    } catch (err) {
      showAlert(extractApiError(err, 'Failed to delete sector.'), 'error');
    }
  };

  const handleFormSubmit = async (sectorData: CreateSectorPayload | UpdateSectorPayload) => {
    if (!editingSector) return;
    hideAlert();
    setIsSubmitting(true);
    try {
      const payload: UpdateSectorPayload = {
        sector_name: sectorData.sector_name ?? editingSector.sector_name,
        image_url: sectorData.image_url !== undefined ? sectorData.image_url : editingSector.image_url,
      };
      const updated = await updateSector(editingSector.id, payload);
      setSectors((prev) => prev.map((s) => (s.id === editingSector.id ? { ...updated.sector, stores: editingSector.stores } : s)));
      showAlert('Sector updated successfully.', 'success');
      setIsModalOpen(false);
      setEditingSector(null);
    } catch (err) {
      showAlert(extractApiError(err, 'Failed to update sector.'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p className={styles.errorMessage}>Access denied. Admin only.</p>
          </main>
        </div>
      </div>
    );
  }

  if (isLoading && sectors.length === 0) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p className={styles.loadingMessage}>Loading sectors...</p>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={layoutStyles.pageContainer}>
      <Header />
      <div className={styles.mainLayout}>
        <SideMenu />
        <main className={styles.contentArea}>
          <Alert message={alert.message} type={alert.type} isVisible={alert.isVisible} onClose={hideAlert} />
          <div className={styles.titleBar}>
            <h2 className={styles.title}>All Sectors</h2>
            <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
          </div>
          <div className={styles.toolbar}>
            <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder="Search sectors..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={styles.searchInput}
              />
              <button type="submit" className={styles.searchButton}>Search</button>
            </form>
            <label className={styles.sortLabel}>
              Sort by:
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [by, order] = e.target.value.split('-') as [string, 'asc' | 'desc'];
                  setSortBy(by);
                  setSortOrder(order);
                  setPage(1);
                }}
                className={styles.sortSelect}
              >
                <option value="id-asc">ID (asc)</option>
                <option value="id-desc">ID (desc)</option>
                <option value="sector_name-asc">Name (A–Z)</option>
                <option value="sector_name-desc">Name (Z–A)</option>
              </select>
            </label>
          </div>
          {sectors.length === 0 && !isLoading ? (
            <p className={styles.emptyState}>No sectors found across all stores.</p>
          ) : viewMode === 'table' ? (
            <div className={styles.tableContainer}>
              <table className={tableStyles.dataTable}>
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Name</th>
                    <th>Stores</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sectors.map((sector) => (
                    <tr key={sector.id}>
                      <td className={tableStyles.imageCell}>
                        {sector.image_url ? (
                          <img
                            src={sector.image_url}
                            alt={sector.sector_name}
                            className={tableStyles.tableImage}
                            onError={(e) => { e.currentTarget.src = placeholderImage; }}
                          />
                        ) : (
                          <div className={tableStyles.tablePlaceholder}>🏭</div>
                        )}
                      </td>
                      <td>{sector.sector_name}</td>
                      <td className={styles.storesCell}>
                        {sector.stores?.length
                          ? sector.stores.map((st) => (
                              <React.Fragment key={st.id}>
                                <Link to={`/store/${st.id}/overview`} className={styles.storeLink}>{st.store_name}</Link>
                                {st !== sector.stores![sector.stores!.length - 1] ? ', ' : null}
                              </React.Fragment>
                            ))
                          : '—'}
                      </td>
                      <td>
                        <div className={styles.actionButtons}>
                          <button
                            type="button"
                            onClick={() => handleEdit(sector)}
                            className={`${styles.actionButton} ${styles.editButton}`}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(sector.id)}
                            className={`${styles.actionButton} ${styles.deleteButton}`}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={cardGridStyles.cardsGrid}>
              {sectors.map((sector) => (
                <SectorCard
                  key={sector.id}
                  sector={sector}
                  onEdit={() => handleEdit(sector)}
                  onDelete={() => handleDelete(sector.id)}
                  onViewProducts={() => {}}
                  canEdit
                />
              ))}
            </div>
          )}
          {total > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={limit}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
              limitOptions={[10, 20, 50]}
              showLimitSelector
              disabled={isLoading}
            />
          )}
          <SectorFormModal
            isOpen={isModalOpen}
            onClose={() => { setIsModalOpen(false); setEditingSector(null); hideAlert(); }}
            onSubmit={handleFormSubmit}
            initialData={editingSector}
            isLoading={isSubmitting}
          />
        </main>
      </div>
    </div>
  );
};

export default AllSectorsPage;
